import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { ArrowLeftIcon } from '@heroicons/react/20/solid';
import ModalBasic from '../components/ModalBasic';
import ChangeStatusModal from './ChangeStatusModal';
import UpdateCampaignName from './updateCampaignName';
import { statuses } from '../data/StatusColours';
import { gameTypes } from '../data/GameTypes';
import { motion } from 'framer-motion';
import { Popover, Transition } from '@headlessui/react';

const getGameTypeColors = (gameTypeId) => {
  const colorMap = {
    "pindrop": {
      bg: "rgba(231, 76, 60, 0.1)",
      icon: "#E74C3C",
      border: "2px solid rgba(231, 76, 60, 0.3)"
    },
    "hint": {
      bg: "rgba(241, 196, 15, 0.1)",
      icon: "#F1C40F",
      border: "2px solid rgba(241, 196, 15, 0.3)"
    },
    "quiz": {
      bg: "rgba(52, 152, 219, 0.1)",
      icon: "#3498DB",
      border: "2px solid rgba(52, 152, 219, 0.3)"
    },
    "mapselect": {
      bg: "rgba(46, 204, 113, 0.1)",
      icon: "#2ECC71",
      border: "2px solid rgba(46, 204, 113, 0.3)"
    },
    "snake": {
      bg: "rgba(155, 89, 182, 0.1)",
      icon: "#9B59B6",
      border: "2px solid rgba(155, 89, 182, 0.3)"
    }
  };
  return colorMap[gameTypeId] || colorMap["quiz"];
};

export default function CampaignHeader({ title, name, status, gameType, onStatusChange, onNameChange }) {
  const navigate = useNavigate();
  const location = useLocation();
  const [isNameModalOpen, setIsNameModalOpen] = useState(false);
  const [localStatus, setLocalStatus] = useState(status);
  const [isTransitioning, setIsTransitioning] = useState(false);

  // Update local status when prop changes
  useEffect(() => {
    setLocalStatus(status);
  }, [status]);

  const handleStatusChange = async (newStatus) => {
    setIsTransitioning(true);
    setLocalStatus(newStatus);
    
    await new Promise(resolve => setTimeout(resolve, 300));
    onStatusChange(newStatus);
    
    setTimeout(() => {
      setIsTransitioning(false);
    }, 200);
  };

  const handleNameChange = (newName) => {
    setTimeout(() => {
      onNameChange(newName);
      setIsNameModalOpen(false);
    }, 200);
  };

  const handleNameClick = () => {
    setTimeout(() => {
      setIsNameModalOpen(true);
    }, 500);
  };

  const handleBack = () => {
    const previousPath = location.state?.from;
    
    // If coming from root, go back to root
    if (previousPath === '/') {
      navigate('/');
      return;
    }
    
    // If coming from editor or no previous path, go to campaigns
    if (!previousPath || previousPath.includes('editor')) {
      navigate('/studio/campaigns');
      return;
    }
    
    // Otherwise use the previous path
    navigate(previousPath);
  };

  const getStatusEmoji = (status) => {
    switch(status) {
      case 'draft': return '📝';
      case 'ready': return '⚡';
      case 'live': return '🔥';
      case 'complete': return '✅';
      default: return '📝';
    }
  };

  return (
    <>
      {/* Update the back button */}
      <div className="flex items-center pb-4">
        <button
          onClick={handleBack}
          className="flex items-center text-sm font-bold tracking-wide transition-all duration-200 p-2 rounded-lg"
          style={{
            fontFamily: '"Share Tech Mono", monospace',
            color: '#F1C40F',
            background: 'rgba(52, 73, 94, 0.6)',
            border: '1px solid rgba(241, 196, 15, 0.4)'
          }}
          onMouseEnter={(e) => {
            e.target.style.background = 'linear-gradient(135deg, #F1C40F, #F39C12)';
            e.target.style.color = '#2C3E50';
            e.target.style.border = '2px solid #E67E22';
          }}
          onMouseLeave={(e) => {
            e.target.style.background = 'rgba(52, 73, 94, 0.6)';
            e.target.style.color = '#F1C40F';
            e.target.style.border = '1px solid rgba(241, 196, 15, 0.4)';
          }}
        >
          <ArrowLeftIcon className="h-5 w-5 mr-2" aria-hidden="true" /> GO BACK
        </button>
      </div>

      {/* Main Content */}
      <div className="flex flex-col gap-y-4 py-2">
        {/* Desktop Layout - Side by side */}
        <div className="hidden md:flex items-center justify-between">
          {/* Left side: Icon and Title */}
          <div className="flex items-center gap-x-4">
            {/* Game Type Icon */}
            {(() => {
              const gameTypeData = gameTypes.find(type => type.id === gameType);
              const colors = getGameTypeColors(gameType);
              
              return gameTypeData?.icon && (
                <div className="p-3 rounded-lg" style={{
                  background: colors.bg,
                  border: colors.border
                }}>
                  <gameTypeData.icon className="w-6 h-6" style={{ color: colors.icon }} />
                </div>
              );
            })()}

            {/* Campaign Name */}
            <div className="relative group">
              <button 
                onClick={handleNameClick}
                className="text-left transition-colors duration-200 p-2 rounded-lg"
                style={{
                  fontFamily: '"Share Tech Mono", monospace'
                }}
                onMouseEnter={(e) => {
                  e.target.style.background = 'rgba(241, 196, 15, 0.1)';
                }}
                onMouseLeave={(e) => {
                  e.target.style.background = 'transparent';
                }}
              >
                <h1 className="text-3xl font-bold leading-7 tracking-wide" style={{
                  color: '#F1C40F',
                  textShadow: '0 0 8px rgba(241, 196, 15, 0.5)',
                  fontFamily: '"Share Tech Mono", monospace'
                }}>
                  {title}
                </h1>
              </button>
              <div className="absolute left-0 bottom-full mb-2 hidden w-max rounded-md text-xs px-2 py-1 group-hover:block"
                style={{
                  background: 'linear-gradient(135deg, #2C3E50, #34495E)',
                  color: '#F1C40F',
                  border: '1px solid rgba(241, 196, 15, 0.4)',
                  fontFamily: '"Share Tech Mono", monospace'
                }}
              >
                ✏️ CHANGE NAME
              </div>
            </div>
          </div>

          {/* Desktop Status Button */}
          <Popover className="relative">
            {({ open, close }) => (
              <>
                <Popover.Button
                  as={motion.button}
                  className="inline-flex items-center gap-x-2 rounded-lg px-4 py-2.5 text-sm font-bold tracking-wide transition-all duration-200 ease-in-out hover:scale-105 hover:shadow-lg focus:outline-none"
                  style={{
                    fontFamily: '"Share Tech Mono", monospace',
                    background: 'linear-gradient(135deg, #2C3E50, #34495E)',
                    color: '#F1C40F',
                    border: '2px solid rgba(241, 196, 15, 0.4)',
                    textShadow: '0 0 4px rgba(241, 196, 15, 0.3)'
                  }}
                  animate={{ opacity: isTransitioning ? 0.7 : 1 }}
                  transition={{ duration: 0.2 }}
                >
                  <span className="text-lg">{getStatusEmoji(localStatus)}</span>
                  <span className="uppercase">{localStatus}</span>
                  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className="w-5 h-5 transform transition-transform duration-200" style={{
                    transform: open ? 'rotate(180deg)' : 'rotate(0deg)'
                  }}>
                    <path fillRule="evenodd" d="M5.23 7.21a.75.75 0 011.06.02L10 11.168l3.71-3.938a.75.75 0 111.08 1.04l-4.25 4.5a.75.75 0 01-1.08 0l-4.25-4.5a.75.75 0 01.02-1.06z" clipRule="evenodd" />
                    </svg>
                  </Popover.Button>

                  <Transition
                    enter="transition duration-200 ease-out"
                    enterFrom="transform scale-95 opacity-0"
                    enterTo="transform scale-100 opacity-100"
                    leave="transition duration-150 ease-in"
                    leaveFrom="transform scale-100 opacity-100"
                    leaveTo="transform scale-95 opacity-0"
                  >
                    <Popover.Panel 
                      className="absolute right-0 z-10 mt-2"
                      static
                    >
                      <ChangeStatusModal
                        currentStatus={status}
                        onChange={handleStatusChange}
                        close={close}
                      />
                    </Popover.Panel>
                  </Transition>
                </>
              )}
            </Popover>
          </div>

          {/* Mobile Layout - Stacked */}
          <div className="md:hidden space-y-4">
            {/* Icon and Title Row */}
            <div className="flex items-center gap-x-4">
              {/* Game Type Icon */}
              {(() => {
                const gameTypeData = gameTypes.find(type => type.id === gameType);
                const colors = getGameTypeColors(gameType);
                
                return gameTypeData?.icon && (
                  <div className="p-3 rounded-lg" style={{
                    background: colors.bg,
                    border: colors.border
                  }}>
                    <gameTypeData.icon className="w-6 h-6" style={{ color: colors.icon }} />
                  </div>
                );
              })()}

              {/* Campaign Name */}
              <div className="relative group flex-1">
                <button 
                  onClick={handleNameClick}
                  className="text-left transition-colors duration-200 p-2 rounded-lg w-full"
                  style={{
                    fontFamily: '"Share Tech Mono", monospace'
                  }}
                  onMouseEnter={(e) => {
                    e.target.style.background = 'rgba(241, 196, 15, 0.1)';
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.background = 'transparent';
                  }}
                >
                  <h1 className="text-2xl md:text-3xl font-bold leading-7 tracking-wide" style={{
                    color: '#F1C40F',
                    textShadow: '0 0 8px rgba(241, 196, 15, 0.5)',
                    fontFamily: '"Share Tech Mono", monospace'
                  }}>
                    {title}
                  </h1>
                </button>
              </div>
            </div>

            {/* Mobile Status Button - Full Width */}
            <Popover className="relative w-full">
              {({ open, close }) => (
                <>
                  <Popover.Button
                    as={motion.button}
                    className="w-full inline-flex items-center justify-center gap-x-2 rounded-lg px-4 py-3 text-sm font-bold tracking-wide transition-all duration-200 ease-in-out focus:outline-none"
                    style={{
                      fontFamily: '"Share Tech Mono", monospace',
                      background: 'linear-gradient(135deg, #2C3E50, #34495E)',
                      color: '#F1C40F',
                      border: '2px solid rgba(241, 196, 15, 0.4)',
                      textShadow: '0 0 4px rgba(241, 196, 15, 0.3)'
                    }}
                    animate={{ opacity: isTransitioning ? 0.7 : 1 }}
                    transition={{ duration: 0.2 }}
                  >
                    <span className="text-lg">{getStatusEmoji(localStatus)}</span>
                    <span className="uppercase">STATUS: {localStatus}</span>
                    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className="w-5 h-5 transform transition-transform duration-200" style={{
                      transform: open ? 'rotate(180deg)' : 'rotate(0deg)'
                    }}>
                      <path fillRule="evenodd" d="M5.23 7.21a.75.75 0 011.06.02L10 11.168l3.71-3.938a.75.75 0 111.08 1.04l-4.25 4.5a.75.75 0 01-1.08 0l-4.25-4.5a.75.75 0 01.02-1.06z" clipRule="evenodd" />
                    </svg>
                  </Popover.Button>

                  <Transition
                    enter="transition duration-200 ease-out"
                    enterFrom="transform scale-95 opacity-0"
                    enterTo="transform scale-100 opacity-100"
                    leave="transition duration-150 ease-in"
                    leaveFrom="transform scale-100 opacity-100"
                    leaveTo="transform scale-95 opacity-0"
                  >
                    <Popover.Panel 
                      className="absolute left-0 right-0 z-10 mt-2"
                      static
                    >
                      <ChangeStatusModal
                        currentStatus={status}
                        onChange={handleStatusChange}
                        close={close}
                      />
                    </Popover.Panel>
                  </Transition>
                </>
              )}
            </Popover>
          </div>

        {/* Name (not clickable) */}
        <p className="text-xl font-medium tracking-wide" style={{
          color: '#BDC3C7',
          fontFamily: '"Share Tech Mono", monospace'
        }}>{name}</p>
      </div>

      {/* Name Change Modal */}
      <ModalBasic
        id="campaign-name-modal"
        title="✏️ CHANGE CAMPAIGN NAME"
        modalOpen={isNameModalOpen}
        setModalOpen={setIsNameModalOpen}
      >
        <UpdateCampaignName
          currentName={title}
          onUpdateCampaignName={handleNameChange}
          onClose={() => setIsNameModalOpen(false)}
        />
      </ModalBasic>
    </>
  );
}
